/** Product mockup: 6 volumes, the practice-plan tablet and the 5 bonus covers. */
export const heroImage = 'https://cdn.magicpatterns.com/uploads/gdAkDyBNHJiuy8mtVGo8hY/Sistema_Visual_de_Baloncesto_Juvenil_360-Photoroom.webp';

/** Payment link used by every checkout button (the hero button scrolls to the offer instead). */
export const checkoutUrl = 'https://youth-basketball-360.impultienda.ar/checkout';
export const brandName = 'YOUTH BASKETBALL 360™';
export const price = '$27';
export const regularPrice = '$97';

/** Sent with every InitiateCheckout event. */
export const checkoutPixelParams = {
  content_name: 'Youth Basketball 360',
  value: 27,
  currency: 'USD'
};
export const heroAudience = 'For youth basketball coaches, PE teachers & parent-coaches';
export const heroImageAlt = 'YOUTH BASKETBALL 360™ complete: 6 visual coaching volumes, 72 ready-to-run 60-minute practices, age-specific progressions, court diagrams, step-by-step skill instruction and 5 practical coaching bonuses.';
export interface Stat {
  value: string;
  label: string;
}
export const stats: Stat[] = [{
  value: '6',
  label: 'Visual coaching volumes'
}, {
  value: '72',
  label: 'Ready-to-run practices'
}, {
  value: '5',
  label: 'Practical coaching bonuses'
}];
export const heroBadges: string[] = ['100% in English', 'Instant access', 'Downloadable PDF', '7-day guarantee'];
export interface Testimonial {
  quote: string;
  role: string;
  country: string;
  /** False until the quote is replaced with a verified testimonial. */
  verified: boolean;
}
export const testimonials: Testimonial[] = [{
  quote: 'Before, I saved drills from everywhere and then spent time trying to connect them. Having the whole practice organized makes planning much easier.',
  role: 'Youth Basketball Coach',
  country: 'United States',
  verified: false
}, {
  quote: 'The biggest difference is clarity. The diagrams, progressions and coaching cues make it easier to explain and keep players moving.',
  role: 'Parent-Coach',
  country: 'United Kingdom',
  verified: false
}];

/** The "20 minutes before practice" scene in the problem section. */
export const arrivalLines: string[] = ['Your players are arriving.', 'Different ages.', 'Different skill levels.', 'Different confidence levels.', 'Limited court space.', 'Not enough basketballs.', 'Very little planning time.'];
export const searchLines: string[] = ['Searching YouTube.', 'Saving drills.', 'Taking screenshots.', 'Opening different websites.', 'Trying to connect random ideas.'];
export const sessionLines: string[] = ['One drill is too easy.', 'Another creates a long line.', 'Some players lose focus.', 'Some struggle to control the ball.', 'Others need a bigger challenge.', 'You explain for too long.', 'One player works while everyone else waits.', 'You see a mistake... but you are improvising the correction.', 'Some players understand what to do.', 'Others look confused.'];

/** The real cost of not having a clear system. */
export type CostIcon = 'clock' | 'hourglass' | 'shuffle' | 'talk' | 'crowd' | 'route' | 'eye';
export interface Cost {
  title: string;
  detail: string[];
  icon: CostIcon;
  /** Accent tint for the card. */
  color: string;
}
export const costs: Cost[] = [{
  icon: 'clock',
  color: '#f97316',
  title: 'Hours of planning',
  detail: ['Your free time disappears before practice even begins.']
}, {
  icon: 'hourglass',
  color: '#f59e0b',
  title: 'Long lines',
  detail: ['One player works while the others stand and watch.']
}, {
  icon: 'shuffle',
  color: '#8b5cf6',
  title: 'Random drills',
  detail: ['You have activities, but no clear development path.']
}, {
  icon: 'talk',
  color: '#ec4899',
  title: 'Too much talking',
  detail: ['Young players need to see and do — not listen for five minutes.']
}, {
  icon: 'crowd',
  color: '#14b8a6',
  title: 'Mixed ability problems',
  detail: ['The same drill feels too easy for some and too difficult for others.']
}, {
  icon: 'route',
  color: '#3b82f6',
  title: 'No clear progression',
  detail: ['You teach a skill, but you are not always sure what should come next.']
}, {
  icon: 'eye',
  color: '#ef4444',
  title: 'Players lose focus',
  detail: ['The activity is not always wrong — sometimes the challenge simply needs adjusting.']
}];
export const finalCost = {
  title: 'Every practice starts from zero',
  lead: 'And the same question comes back:',
  question: '“What am I going to do with them today?”'
};

/** "Open any volume and quickly see:" */
export const volumeChecks: string[] = ['What to teach', 'How to set it up', 'Where players move', 'What coaching cue to use', 'What mistake to watch for', 'How to make it easier', 'How to make it harder', 'How to turn the skill into real play'];

/** Coaching for the first time: the five things you need to know. */
export const firstTimeSteps: string[] = ['What to set up', 'What to say', 'What players do', 'What to correct', 'What to do next'];
export const systemFlow = 'Open → Look → Set Up → Coach';

/** Built to be visual: what you will see inside. */
export const visualFeatures: string[] = ['Step-by-step skill sequences', 'Court diagrams', 'Player movement arrows', 'Pass paths', 'Wrong vs right examples', 'Coach cues', 'Common mistakes', 'Easy versions', 'Harder versions', 'Practice timelines', '1v1 progressions', '2v2 progressions', '3v3 progressions', 'Mixed-level adaptations', 'Ready-to-run sessions'];

/** Offer card: what you get, shown above the price button. */
export const offerSummary: string[] = ['6 visual coaching volumes', '72 ready-to-run practices', '5 practical coaching bonuses'];
export const included: string[] = ['Step-by-step basketball technique', 'Visual youth basketball drills', 'Court diagrams', 'Age-specific progressions', 'Player movement sequences', 'Ball-handling progressions', 'Passing progressions', 'Shooting & finishing fundamentals', 'Defensive positioning', 'Rebounding basics', 'Small-sided games', 'Common mistakes + quick fixes', '1v1 situations', '2v1 situations', '2v2 situations', '3v3 games', 'Mixed-level adaptations', 'Coaching cues', 'Practice timelines', 'Practice organization tools', 'Printable coaching resources', '72 complete practices', 'Instant PDF download'];

/** The difference: keep improvising vs. the system. */
export const differenceWithout: string[] = ['Hours searching for drills', 'Long lines', 'Too much explanation', 'No clear development path', 'Mixed ability problems', 'Players losing focus', 'Not enough basketballs', 'Random weekly planning', 'Every practice starts from zero'];
export const differenceWith: string[] = ['5 minutes to organize your practice', '72 complete ready-to-run sessions', 'Clear step-by-step progressions', 'More movement, less waiting', 'Visual youth basketball coaching system', 'Age-specific adaptations', 'Simple coaching cues', '1v1, 2v2 and 3v3 progressions', 'Solutions for limited space and equipment', 'A repeatable structure for every week'];

/** "You need a system that helps you:" */
export const systemBenefits: string[] = ['Plan faster.', 'Teach more clearly.', 'Keep players moving.', 'Correct mistakes quickly.', 'Adapt to different levels.', 'Build better practices week after week.'];
